import { Component, Input, EventEmitter, Output, OnChanges, ViewChild } from '@angular/core';
import { RemoteControlDeviceState, DeviceStateRegistryService } from '../../services/device-state-registry.service';
import { DisplayMode } from '../toolbar-popover/toolbar-popover.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { FileManagerUploadContentDialogComponent } from '../file-manager-upload-content-dialog/file-manager-upload-content-dialog.component';
import { FileManagerService } from '../../services/file-manager.service';
import { FileSearchState } from '../../file-manager/FileManagerState';
import { SotiPopupTrigger } from '@soti/popup';
import { XsI18nPipe } from '@nx-fe/pipes';
import { DEVICE_OS_FAMILY, FeatureToggleService, hd } from '@nx-fe/services';
import {
  WorkPanelBarItem,
  WorkPanelBarItemType,
} from 'xsmodules/ticket/components/work-panel/models/work-panel-bar-items';
import { ContentStatus } from 'xsmodules/remote-control/file-manager/fileData';

@Component({
  selector: 'app-file-manager-buttons',
  templateUrl: './file-manager-buttons.component.html',
  styleUrls: ['./file-manager-buttons.component.scss'],
})
export class FileManagerButtonsComponent implements OnChanges {
  @Input() rcState: RemoteControlDeviceState;
  @Output() showFileManagerButtons = new EventEmitter<boolean>();
  @ViewChild(SotiPopupTrigger, { static: false }) statusCenterPopupTrigger: SotiPopupTrigger;

  public displayMode = DisplayMode;
  public isPopUpOpened: boolean;

  private cdkOverlayPaneClass = '.cdk-overlay-pane';
  private knownChildElementName = 'app-file-manager-status-center';
  private statusCenterSotiPopupClass = 'status-center-soti-popup-position';
  private hasFailedTabSelected: boolean;

  private buttonItems: WorkPanelBarItem[] = [
    {
      name: 'Upload',
      icon: () => 'hd-icon-upload',
      type: WorkPanelBarItemType.Button,
      tooltip: () =>
        this.isSearchStateActive()
          ? this.saI18nPipe.transform('fileManager_btn_Upload_Disabled')
          : this.saI18nPipe.transform('fileManager_btn_Upload'),
      onClick: button => {
        if (!button.isDisabled()) {
          this.openContentUploadDialog();
        }
      },
      isVisible: () => this.isUploadSupported() && this.isUploadButtonAvailable(),
      isDisabled: () => this.isSearchStateActive(),
      selected: () => false,
    } as WorkPanelBarItem,
    {
      name: 'Refresh',
      icon: () => 'hd-icon-refresh',
      type: WorkPanelBarItemType.Button,
      tooltip: () => this.saI18nPipe.transform('fileManager_btn_Refresh'),
      onClick: () => {},
      isVisible: () =>
        this.isUploadSupported() && this.featureToggleService.isEnabled('FileManagerRefreshAndAddFolderButton'),
      isDisabled: () => false,
      selected: () => false,
    } as WorkPanelBarItem,
    {
      name: 'AddFolder',
      icon: () => 'hd-icon-newfolder',
      type: WorkPanelBarItemType.Button,
      tooltip: () => this.saI18nPipe.transform('fileManager_btn_AddFolder'),
      onClick: () => {},
      isVisible: () => this.isUploadSupported() && this.isAddFolderButtonAvailable(),
      isDisabled: () => false,
      selected: () => false,
    } as WorkPanelBarItem,
    {
      name: 'StatusCenter',
      icon: () => 'hd-icon-notifications',
      type: WorkPanelBarItemType.Button,
      tooltip: () => this.saI18nPipe.transform('fileManager_btn_StatusCenter'),
      onClick: () => {},
      isVisible: () => true,
      isDisabled: () => false,
      selected: () => this.isPopUpOpened,
    } as WorkPanelBarItem,
  ];

  constructor(
    private featureToggleService: FeatureToggleService,
    private saI18nPipe: XsI18nPipe,
    private deviceStateRegistry: DeviceStateRegistryService,
    private dialog: MatDialog,
    private fileManagerService: FileManagerService
  ) {}

  ngOnChanges() {
    this.getVisibleButtons().length > 0
      ? this.showFileManagerButtons.emit(true)
      : this.showFileManagerButtons.emit(false);
  }

  public isOneItemOnly = (): boolean => this.getVisibleButtons().length === 1;

  public getVisibleButtons = (): WorkPanelBarItem[] => this.buttonItems.filter((button: WorkPanelBarItem) => button.isVisible());

  public openContentUploadDialog = () => {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'FileManagerUploadContentDialog';
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.panelClass = 'custom-dialog-container';
    dialogConfig.height = 'auto';
    dialogConfig.width = '660px';
    this.dialog.open(FileManagerUploadContentDialogComponent, dialogConfig);
  };

  public isSotiPopup = (button: WorkPanelBarItem): boolean => button.name === 'StatusCenter';

  public popupOpened = (isOpened: boolean) => {
    this.isPopUpOpened = isOpened;

    // We need to assign a specific class to reposition the soti popup.
    // For that we should find the closest element with the cdk-overlay-pane (added by the soti popup we want)
    // as we know it is within the SotiPopup we need to reposition
    // const element = $(this.knownChildElementName).closest(this.cdkOverlayPaneClass);
    // if (isOpened) {
    //     element.addClass(this.statusCenterSotiPopupClass);
    // } else {
    //     element.removeClass(this.statusCenterSotiPopupClass);
    // }

    this.clearFailedFlag();
  };

  public statusCenterTabChanged = (isFailedTabOpen: boolean) => {
    this.hasFailedTabSelected = isFailedTabOpen;
    if (this.isPopUpOpened && this.hasFailedTabSelected) {
      this.clearFailedFlag();
    }
  };

  public isFailedIndicatorActive = (): boolean => (
      this.fileManagerService.isFieldIndicatorActive &&
      (!this.isPopUpOpened || (this.isPopUpOpened && !this.hasFailedTabSelected))
    );

  public isAnyActionsInProgress = (): boolean => (
      !this.isPopUpOpened &&
      Array.from(this.fileManagerService.getStatusCenterData().values()).filter(
        x => x.contentStatus === ContentStatus.InProgress || x.contentStatus === ContentStatus.CompressInProgress
      ).length > 0
    );

  public getPopupTrigger = (): SotiPopupTrigger => this.statusCenterPopupTrigger;

  //May be joined with the isAddFolderButtonAvailable when toggles FileManagerRefreshAndAddFolderButton and FileManagerUploadButton will have been removed and vice-versa.
  private isUploadButtonAvailable = () => this.hasDeviceWritePermission(hd.RemoteControlPermissions.RemoteControlUpdateFiles);

  //May be joined with the isUploadButtonAvailable when toggles FileManagerRefreshAndAddFolderButton and FileManagerUploadButton will have been removed and vice-versa.
  private isAddFolderButtonAvailable = () => (
      this.featureToggleService.isEnabled('FileManagerRefreshAndAddFolderButton') &&
      this.hasDeviceWritePermission(hd.RemoteControlPermissions.RemoteControlUpdateFiles)
    );

  //Do not need view permission because if no view permission then buttons is not shown
  private hasDeviceWritePermission = (permission: hd.RemoteControlPermissions): boolean => {
    if (this.rcState && this.rcState.device) {
      const devicePermissions = this.deviceStateRegistry.getPermissions(this.rcState.device.deviceId);
      return devicePermissions && devicePermissions.permissions && (devicePermissions.permissions & permission) !== 0;
    }
  };

  private clearFailedFlag = () => {
    if (this.hasFailedTabSelected) {
      this.fileManagerService.isFieldIndicatorActive = false;
    }
  };

  private isSearchStateActive = (): boolean => {
    const state = this.fileManagerService.getState(this.rcState.device.deviceId);
    return state && state.searchState && state.searchState !== FileSearchState.Ready;
  };

  private isUploadSupported = () => !this.isAppleDevice();

  private isAppleDevice = (): boolean => {
    if (this.rcState && this.rcState.device && this.rcState.device.family) {
      return this.rcState.device.family.indexOf(DEVICE_OS_FAMILY.Apple) >= 0;
    } else {
      return false;
    }
  };
}
